import React from "react";
import { Loader2, Upload, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EVIDENCE_TYPES } from "./contractAdjustmentsUtils";
import type { AvailableDocument, AttachedEvidence } from "./useContractAdjustmentEvidence";

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  plan: "Plano", project: "Proyecto", safety_plan: "Plan de seguridad",
  contract: "Contrato", permit: "Permiso", specification: "Pliego",
  conformity: "Conformidad", other: "Otro",
};

export type ContractAdjustmentEvidenceStepProps = {
  availableDocuments: AvailableDocument[];
  attachedEvidence: AttachedEvidence[];
  attachedDocIds: Set<number>;
  isLoadingEvidence: boolean;
  isUploading: boolean;
  canSubmitEvidence: boolean;
  attachExisting: (documentId: number, documentName: string) => Promise<void>;
  detachEvidence: (documentId: number) => Promise<void>;
  updateEvidenceType: (documentId: number, evidenceType: string) => Promise<void>;
  uploadAndAttach: (file: File) => Promise<void>;
  isAnyPending: boolean;
};

export function ContractAdjustmentEvidenceStep(props: ContractAdjustmentEvidenceStepProps) {
  const {
    availableDocuments, attachedEvidence, attachedDocIds,
    isLoadingEvidence, isUploading, canSubmitEvidence,
    attachExisting, detachEvidence, updateEvidenceType, uploadAndAttach,
    isAnyPending,
  } = props;

  const [showUploadInput, setShowUploadInput] = React.useState(false);

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAndAttach(file).then(() => setShowUploadInput(false));
    }
  };

  return (
    <div className="grid gap-4 py-2">
      {/* Section header */}
      <div>
        <h4 className="mb-1 font-medium">Evidencias</h4>
        <p className="text-sm text-muted-foreground">
          Selecciona documentos existentes o sube nuevos como evidencias de la modificación.
          Para enviar la modificación debe haber al menos una evidencia cualificante
          (anexo firmado, contrato firmado, pedido o aceptación por correo).
        </p>
      </div>

      {/* Upload section */}
      {!showUploadInput ? (
        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowUploadInput(true)}
            disabled={isUploading || isAnyPending}
          >
            <Upload className="mr-2 h-4 w-4" />
            Subir nuevo documento
          </Button>
        </div>
      ) : (
        <div className="space-y-3 rounded-md border p-4">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-medium">Subir documento</h5>
            <Button variant="ghost" size="sm" onClick={() => setShowUploadInput(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="ev-upload-file">Archivo (PDF o imagen)</Label>
            <Input
              id="ev-upload-file"
              type="file"
              accept="application/pdf,image/*"
              onChange={handleFileSelected}
              disabled={isUploading || isAnyPending}
            />
          </div>
          {isUploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Subiendo...
            </div>
          )}
        </div>
      )}

      {/* Existing documents selector */}
      {isLoadingEvidence ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando documentos...
        </div>
      ) : (
        <div className="space-y-2">
          <h5 className="text-sm font-medium">Documentos existentes en la instalación</h5>
          {availableDocuments.length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              No hay documentos disponibles. Puedes subir uno nuevo.
            </div>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {availableDocuments.map(doc => {
                const isAttached = attachedDocIds.has(doc.id);
                return (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => {
                      if (isAttached) {
                        detachEvidence(doc.id);
                      } else {
                        attachExisting(doc.id, doc.name);
                      }
                    }}
                    disabled={isAnyPending}
                    className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border p-3 text-left text-sm transition-colors disabled:opacity-50 ${
                      isAttached
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{doc.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}
                      </div>
                    </div>
                    {isAttached && (
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        Vinculado
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Attached evidence list with type selector */}
      {attachedEvidence.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium">
            Evidencias seleccionadas ({attachedEvidence.length})
          </h5>
          <div className="space-y-2">
            {attachedEvidence.map(doc => (
              <div
                key={doc.documentId}
                className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{doc.documentName}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={doc.evidenceType}
                    onValueChange={value => updateEvidenceType(doc.documentId, value)}
                    disabled={isAnyPending}
                  >
                    <SelectTrigger className="h-8 w-40 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVIDENCE_TYPES.map(et => (
                        <SelectItem key={et.value} value={et.value}>
                          {et.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => detachEvidence(doc.documentId)}
                    disabled={isAnyPending}
                    className="h-8 px-2 text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Quotation/warning */}
          {!canSubmitEvidence && attachedEvidence.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Para enviar a aprobación debe haber al menos una evidencia cualificante
              (anexo firmado, contrato firmado, pedido o aceptación por correo).
            </div>
          )}
        </div>
      )}
    </div>
  );
}